/* @flow */
import { observable, action, when, autorun } from 'mobx';
import moment from 'moment';
import fileDownload from 'js-file-download';

import endpoints from 'api/endpoints';
import { activities } from 'components/Activity/ActivityData';
import { asset, account, manufacturer, product, location, generateUrl } from 'api/routes';
import type { DataSource, CustomerAccount } from 'models/CustomerAccounts';

import { ApiModel } from '../api/ApiModel';
import ReportViewStore from './ReportViewStore';
import { getStore } from '../services/ServiceRegistry';

export type ExportTypes = 'rental' | 'owned' | 'asset-request' | 'all' | undefined;

export type LoadingStoreEntry = {
  [key: string]: boolean,
};

export type ActionDrawerInstance = {
  [key: string]: boolean,
};

const ExportTypeEnum = {
  RETURNED: 'returned',
  RENTAL: 'rental',
  PURCHASED: 'purchase',
};

export default class ActionDrawerStore extends ApiModel {
  @observable actionDrawerOpen: boolean = false;
  @observable currentActionDrawer: string = '';
  @observable currentActionDrawerData: Object = {};
  @observable currentActionCallback: ?Function;
  @observable documentError: boolean = false;
  @observable success: boolean = false;
  @observable error: boolean = false;
  @observable errorMessage: ?string;
  @observable errorResponse: Object;
  @observable loading: boolean = false;
  @observable loadingStore: Map<LoadingStoreEntry> = new Map();
  @observable loadParent: boolean = false;
  @observable formSubmittedData: Object = {};
  @observable response: Object = {};
  @observable locationColumn = 1;
  @observable URLConfig = {
    routeWithoutActionType: '',
    goToNormalRoute: null,
    goToActionTypeRoute: null,
  };

  @observable suppressReportViewCacheInvalidation: boolean = false;

  constructor(props) {
    super(props);

    autorun(() => {
      if (this.suppressReportViewCacheInvalidation) {
        return;
      }

      if (this.success || this.error || this.loading) {
        const reportViewStore = getStore(['reportViewStore'])[0];

        if (!reportViewStore.shouldInvalidateCache) {
          reportViewStore.shouldInvalidateCache = true;
        }
      }
    });
  }

  getLoadingEntry(type: string) {
    return this.loadingStore.get(type);
  }

  @action setLoadingEntry(type: string, loading: boolean) {
    return this.loadingStore.set(type, loading);
  }

  @action openActionDrawer(currentAction: string, data: Object, callback?: Function) {
    this.clearActionDrawer();

    this.currentActionDrawer = currentAction;
    this.currentActionCallback = callback;

    if (data) {
      this.currentActionDrawerData = data;
    }

    if (this.URLConfig.goToActionTypeRoute) {
      this.URLConfig.goToActionTypeRoute();
    } else {
      this.actionDrawerOpen = true;
    }
  }

  @action closeActionDrawer() {
    if (this.success) this.loadParent = true;
    if (this.currentActionCallback) this.currentActionCallback();
    if (this.URLConfig.goToNormalRoute) this.URLConfig.goToNormalRoute();
    else this.clearActionDrawer();
  }

  @action clearActionDrawer() {
    this.actionDrawerOpen = false;
    this.currentActionDrawer = '';
    this.success = false;
    this.error = false;
    this.loading = false;
  }

  @action
  async unarchiveAsset(assetGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.unarchived.tag; // eslint-disable-line  camelcase

    try {
      const url: string = `assets/${assetGuid}/unarchive`;
      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async archiveAsset(assetGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.archived.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(
        generateUrl(asset.archiveAsset, [assetGuid]),
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async archiveAccount(accountGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.archived.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(`accounts/${accountGuid}/archive`, formInputs); //`

      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async unarchiveAccount(accountGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.unarchived.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(`accounts/${accountGuid}/unarchive`, formInputs);

      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  downloadCSVFile(data: string, filename: string) {
    fileDownload(data, filename + '.csv', 'text/csv');
  }

  @action async getUrlAsCsvDownload(url: string, filename: string = ''): void {
    const data = await this.api.getBlobResource(url);
    this.downloadCSVFile(data, `${moment(new Date()).format('YYYY-MM-DD')}-${filename}`);
  }

  @action
  exportAssetData(accountId: string, type: ExportTypes): Promise<void> {
    let url: string = '';
    let fileName: string = '';
    if (type === 'asset-request') {
      url = `asset-requests/export/csv?search=account_id:${accountId}`;
    } else {
      url = type
        ? `products/export/csv?search=account_id:${accountId};export_type:${type}-only`
        : `products/export/csv?search=account_id:${accountId}`;
      fileName = type === ExportTypeEnum.RENTAL ? `assets_on_${type}` : `assets_${type}`;
    }

    return this.getUrlAsCsvDownload(url, fileName);
  }

  @action
  exportPdfFile(url: string, filename: string): Promise<void> {
    return this.getUrlAsPdfDownload(url, filename);
  }

  @action
  async openPdf(url: string, filename: string): void {
    this.suppressReportViewCacheInvalidation = true;
    this.success = false;
    this.error = false;
    this.loading = true;

    try {
      const data: object = await this.api.getPdfResource(url);

      const file = new Blob([data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);

      window.open(fileURL);
    } catch (e) {
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    } finally {
      this.loading = false;
      this.suppressReportViewCacheInvalidation = false;
    }
  }

  @action async getUrlAsPdfDownload(url: string, filename: string): void {
    this.suppressReportViewCacheInvalidation = true;
    this.success = false;
    this.error = false;
    this.loading = true;

    try {
      const data: string = await this.api.getPdfResource(url);

      if (data) {
        this.downloadPdfFile(data, moment(new Date()).format('YYYY-MM-DD') + '-' + filename);
      }
    } catch (e) {
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    } finally {
      this.loading = false;
      this.suppressReportViewCacheInvalidation = false;
    }
  }

  downloadPdfFile(data: string, filename: string) {
    fileDownload(data, filename + '.pdf', 'application/pdf');
  }

  getMimeType(type: string) {
    let mimeType = '';

    switch (type) {
      case 'pdf':
      case 'ppt':
      case 'pptx':
      case 'xlsx':
      case 'xls':
      case 'docx':
      case 'doc':
        mimeType = `application/${type}`;
        break;
      case 'jpeg':
      case 'jpg':
      case 'png':
      case 'gif':
        mimeType = `image/${type}`;
        break;
      case 'csv':
        mimeType = 'text/csv';
        break;
      default:
        mimeType = 'text/plain';
        break;
    }

    return mimeType;
  }

  downloadGlobalFile(data: string, filename: string, mimeType: string) {
    fileDownload(data, filename, mimeType);
  }

  @action async getUrlAsGlobalDownload(url: string, filename: string = '', type: string): void {
    this.loading = true;
    let data: string = '';
    const mimeType = this.getMimeType(type);

    type === 'pdf' ||
    type === 'png' ||
    type === 'gif' ||
    type === 'jpg' ||
    type === 'jpeg' ||
    type === 'ppt' ||
    type === 'pptx' ||
    type === 'xlsx' ||
    type === 'xls' ||
    type === 'doc' ||
    type === 'docx'
      ? (data = await this.api.getGlobalResource(url, mimeType, true))
      : (data = await this.api.getGlobalResource(url, mimeType));

    data && this.downloadGlobalFile(data, filename, mimeType);
    this.success = true;
    this.loading = false;
  }

  @action
  async enableDisableAccount(accountGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    /* eslint-disable */
    if (formInputs.is_enabled) {
      formInputs['activity_tag'] = activities.enabled.tag;
    } else {
      formInputs['activity_tag'] = activities.disabled.tag;
    }

    /* eslint-enable */

    try {
      this.response = await this.api.putResource(
        `accounts/${accountGuid}/enable-disable`,
        formInputs
      );

      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async enableDisableAccountMulti(formInputs: Object): Promise<void> {
    this.loading = true;
    /* eslint-disable */
    if (formInputs.is_enabled) {
      formInputs['activity_tag'] = activities.enabled.tag;
    } else {
      formInputs['activity_tag'] = activities.disabled.tag;
    }
    /* eslint-enable */

    try {
      this.response = await this.api.putResource('accounts/multi-enable-disable', formInputs);

      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async reportAssetFault(assetId: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.reportFault.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.createResource(
        `assets/${assetId}/request-fault-report`,
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async scheduleAssetCalibration(assetGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.requestCalibration.tag; // eslint-disable-line  camelcase

    try {
      const url: string = `assets/${assetGuid}/request-calibration-schedule`;

      this.response = await this.api.createResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async scheduleAssetCalMulti(formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.requestCalibration.tag; // eslint-disable-line camelcase

    try {
      const url: string = 'assets/request-multi-calibration-schedule';

      this.response = await this.api.createResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async allocateAssetToProject(
    assetGuid: string,
    projectId: string,
    request: boolean = true
  ): Promise<void> {
    this.loading = true;

    try {
      const endpoint: string = request ? 'request-allocate-project' : 'allocate-project';
      const url: string = `${endpoints.accountAssets}/${assetGuid}/${endpoint}/${projectId}`;

      this.response = await this.api.putResource(url, {
        activity_tag: request
          ? activities.requestAssignedProject.tag
          : activities.assignedProject.tag,
      });
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorMessage = e.message;
    }

    this.loading = false;
  }

  @action
  async removeAssetFromProject(assetGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.removedProject.tag; // eslint-disable-line  camelcase

    try {
      const url: string = `${endpoints.accountAssets}/${assetGuid}/remove-from-project`;

      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async requestAssetQuote(assetGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.requestQuote.tag; // eslint-disable-line  camelcase

    try {
      const url: string = `${endpoints.accountAssets}/${assetGuid}/request-quote`;

      this.response = await this.api.createResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async requestAssetOwnership(assetGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.requestOwnership.tag; // eslint-disable-line camelcase

    try {
      const url: string = `${endpoints.accountAssets}/${assetGuid}/request-ownership`;

      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async requestOwnershipMulti(formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.requestOwnership.tag; // eslint-disable-line camelcase

    try {
      const url: string = `${endpoints.accountAssets}/request-multi-ownership`;

      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }
  @action
  async requestCaaSActions(actionId: String, formInputs: Object): Promise<void> {
    this.loading = true;
    try {
      const url: string = `caas/side-panel/${actionId}`;

      this.response = await this.api.createResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async changeAssetLocation(assetGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.assignedLocation.tag; // eslint-disable-line camelcase

    try {
      const url: string = `assets/${assetGuid}/change-asset-location`;

      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async archiveLocation(locationId: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.archived.tag; // eslint-disable-line camelcase

    try {
      const url: string = `locations/${locationId}/archive`;

      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async unarchiveLocation(
    locationId: string,
    formInputs: Object,
    isCompany: boolean = false
  ): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.unarchived.tag; // eslint-disable-line camelcase

    try {
      const url: string = `${isCompany ? 'companies' : 'locations'}/${locationId}/unarchive`;

      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async markAsDefaultLocation(location: Object) {
    this.loading = true;

    try {
      const url = `accounts/${location.accountId}/location/${location.locationId}/mark-as-default`;

      this.response = await this.api.putResource(url);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async unmarkAsDefaultLocation(location: Object) {
    this.loading = true;

    try {
      const url = `accounts/${location.accountId}/location/${
        location.locationId
      }/unmark-as-default`;

      this.response = await this.api.putResource(url);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async archiveLocationsByCompany(companyId: string, locations: array): Promise<void> {
    this.loading = true;

    try {
      const url = `company/${companyId}/locations/archive`;

      this.response = await this.api.putResource(url, locations);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async unarchiveLocationByCompany(companyId: string, locations: array): Promise<void> {
    this.loading = true;

    try {
      const url = `company/${companyId}/locations/unarchive`;

      this.response = await this.api.putResource(url, locations);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async changeUserLocation(accountId: string, userId: string, locationId: string): Promise<void> {
    this.loading = true;

    const activity_tag = activities.assignedLocation.tag;

    try {
      const url = `accounts/${accountId}/users/${userId}/locations/${locationId}/assign`;

      this.response = await this.api.putResource(url, { activity_tag });
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async removeUserLocation(accountId: string, userId: string, locationId: string): Promise<void> {
    this.loading = true;

    const activity_tag = activities.removedLocation.tag;

    try {
      const url = `accounts/${accountId}/users/${userId}/locations/${locationId}/remove`;

      this.response = await this.api.putResource(url, { activity_tag, location });
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async assignLocationToUsers(locationId: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.assignedUser.tag; // eslint-disable-line camelcase

    try {
      const url: string = `locations/${locationId}/assign-users`;

      this.response = await this.api.createResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async removeLocationFromUsers(locationId: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.removedUser.tag; // eslint-disable-line camelcase

    try {
      const url: string = `locations/${locationId}/remove-users`;

      this.response = await this.api.deleteResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async startAuditByLocations(formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.resetAudit.tag; // eslint-disable-line camelcase

    try {
      const url: string = 'assets/reset-audit-asset-location';

      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async startAuditByUsers(formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.resetAudit.tag; // eslint-disable-line camelcase

    try {
      const url = 'assets/reset-audit-asset-user';

      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async selfAuditMultiByLocation(formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.assetAudit.tag; // eslint-disable-line camelcase

    try {
      const url: string = 'assets/audit-asset-location';

      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async reassignAsset(assetGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.assignedAsset.tag; // eslint-disable-line  camelcase

    try {
      const url: string = `assets/${assetGuid}/assign-ownership-asset`;
      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async unassignUserFromAsset(assetId: string): Promise<void> {
    this.loading = true;

    const activityTag = activities.unassignUser.tag;

    try {
      const url = `assets/${assetId}/unassign-user`;

      this.response = await this.api.putResource(url, {
        activity_tag: activityTag,
      });
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async assignAccount(accountId: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.assignedUser.tag; // eslint-disable-line camelcase

    try {
      const url: string = `accounts/${accountId}/assign-users`;

      this.response = await this.api.createResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async reassignProject(projectGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.assignedProject.tag; // eslint-disable-line camelcase

    try {
      const url: string = `projects/${projectGuid}/lead`;
      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async archiveProject(projectGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.archived.tag; // eslint-disable-line camelcase

    try {
      this.response = await this.api.putResource(`projects/${projectGuid}/archive`, formInputs);
      //await this.api.putResource(generateUrl(project.archiveProject, [projectGuid]));
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async unarchiveProject(projectGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.unarchived.tag; // eslint-disable-line camelcase

    try {
      this.response = await this.api.putResource(`projects/${projectGuid}/unarchive`, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async extendProject(projectGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.extended.tag; // eslint-disable-line camelcase

    try {
      this.response = await this.api.putResource(`projects/${projectGuid}/extend`, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async extendProjectMulti(formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.extended.tag; // eslint-disable-line camelcase

    try {
      this.response = await this.api.putResource('projects/extend', formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async extendContract(contractId: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.extendContract.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.createResource(
        `contracts/${contractId}/asset-rental-request`,
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async endProject(projectGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.ended.tag; // eslint-disable-line camelcase

    try {
      this.response = await this.api.putResource(`projects/${projectGuid}/end`, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async endProjectMulti(formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.ended.tag; // eslint-disable-line camelcase

    try {
      this.response = await this.api.putResource('projects/end', formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async assignCostCentreToProject(projectGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.assignCostCenter.tag; // eslint-disable-line camelcase

    try {
      this.response = await this.api.putResource(
        `projects/${projectGuid}/cost-centres`,
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async removeCostCentreFromProject(projectGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.removeCostCenter.tag; // eslint-disable-line camelcase

    try {
      this.response = await this.api.deleteResource(
        `projects/${projectGuid}/cost-centres`,
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async toggleCostCenterStatus(costCenter: Object): Promise<void> {
    this.loading = true;

    const activity_tag = costCenter.status ? 'disable_cost_center' : 'activate_cost_center';

    try {
      this.response = await this.api.putResource(`cost-centres/${costCenter.id}/toggle-status`, {
        activity_tag,
      });
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async assignEngineersToProject(projectGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.assignedUser.tag; // eslint-disable-line camelcase

    try {
      this.response = await this.api.putResource(`projects/${projectGuid}/engineers`, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async removeEngineersFromProject(projectGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.removedUser.tag; // eslint-disable-line camelcase

    try {
      this.response = await this.api.deleteResource(
        `projects/${projectGuid}/engineers`,
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action async extendRentalAsset(assetGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.extended.tag; // eslint-disable-line  camelcase

    try {
      const url: string = 'assets/' + assetGuid + '/extend-rental';

      this.response = await this.api.putResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  /*
    @action extendRental(endDate: string, request: string, reason: string): Promise<*> {
        console.log('%cEndDate - ' + endDate, 'color:green');
        console.log('%cRequestDetails - ' + request, 'color:blue');
        console.log('%cReason - ' + reason, 'color:purple');

        try {
            const url: string = endpoints.accountAssets + '/' + assetGuid + '/extend-rental';

            this.response = await this.api.puteResource(url, formInputs);
            this.success = true;
        } catch (e) {
            this.error = true;
        }

        this.loading = false;
    }
    */

  @action async archiveCostCenter(CostCenterId: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.archived.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(
        `cost-centres/${CostCenterId}/archive`,
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action async unarchiveCostCenter(CostCenterId: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.unarchived.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(
        `cost-centres/${CostCenterId}/unarchive`,
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action async loadProjectsPerAccount(id: string): Promise<void> {
    this.loading = true;

    try {
      this.details = await this.api.getResource(
        'projects?search=' + encodeURIComponent('account:') + id
      );
      //this.success = true;
      this.getPrjc = true;
    } catch (e) {
      console.log(e, 'catching ERRORS');
    }

    this.loading = false;
  }

  @action async assignProjectsToCC(CostCenterId: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.assignProjectsToCostCenter.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(
        `cost-centres/${CostCenterId}/projects`,
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action async loadProjectsPerCostCenter(id: string): Promise<void> {
    this.loading = true;

    try {
      this.detailsPerCC = await this.api.getResource(`cost-centres/${id}/projects`);
      //this.success = true;
      this.getPrjc = true;
    } catch (e) {
      console.log(e, 'catching ERRORS');
    }

    this.loading = false;
  }

  @action async removeProjectsFromCC(CostCenterId: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.removeProjectsFromCostCenter.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.deleteResource(
        `cost-centres/${CostCenterId}/projects`,
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
      this.errorMessage = this.api.oAuthStore.error;
    }

    this.loading = false;
  }

  @action
  async changeUserRole(userGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.roleChanged.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(
        generateUrl(account.changeUserRole, [userGuid]),
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async impersonateLogin(accountID: string, formInputs: Object): Promise<void> {
    this.loading = true;
    // formInputs.activity_tag = activities.loggedInAs.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.createResource(
        generateUrl(account.impersonateLogin, [accountID]),
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async impersonateView(accountID: string, formInputs: Object): Promise<void> {
    this.loading = true;
    // formInputs.activity_tag = activities.viewedBy.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.createResource(
        generateUrl(account.impersonateView, [accountID]),
        formInputs
      );

      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async enableUserAccess(accountID: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.accessEnabled.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(
        generateUrl(account.enableUserAccess, [accountID]),
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async disableUserAccess(accountID: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.accessDisabled.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(
        generateUrl(account.disableUserAccess, [accountID]),
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action async removeUserAccess(accountId: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.accessRevoked.tag; // eslint-disable-line  camelcase

    try {
      const url: string = generateUrl(account.removeUserAccess, [accountId]);

      this.response = await this.api.deleteResource(url, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
    }

    this.loading = false;
  }

  @action
  async userAddManufacturer(formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.addManufacturer.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.createResource(manufacturer.addForUser, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async userAddProduct(formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.addProduct.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.createResource(product.addForUser, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  /**
   * Apply mutually-exclusive location flags into `wire`.
   * mode: 'rush' | 'asset' | null
   * type: 'customer' | 'delivery' | null
   */
  _applyLocationFlags(wire: any, mode: ?('rush' | 'asset'), type: ?string) {
    // Remove both groups first (ensures exclusivity)

    delete wire.asset_request_location;
    delete wire.asset_request_location_type;
    delete wire.rush_asset_request_location;
    delete wire.rush_asset_request_location_type;

    if (mode === 'rush' && (type === 'customer' || type === 'delivery')) {
      wire.rush_asset_request_location = true;

      wire.rush_asset_request_location_type = type;
    } else if (mode === 'asset' && (type === 'customer' || type === 'delivery')) {
      wire.asset_request_location = true;
      wire.asset_request_location_type = type;
    }
  }

  _deduceModeFromAccount(accountStore: any): 'asset' | 'rush' | null {
    const searchType = accountStore?.searchType;
    const details = accountStore?.details?.details;
    const isAsset = Boolean(searchType !== 'rental' && details?.asset_request_form_enabled);
    const isRush = Boolean(searchType !== 'rental' && details?.is_rush_asset_request_form_customer);
    if (isRush) return 'rush';
    if (isAsset) return 'asset';
    return null;
  }

  @action
  async userAddCompany(formInputs: Object, a2?: any, a3?: any, a4?: any, a5?: any): Promise<void> {
    this.loading = true;
    try {
      const wire: any = { ...formInputs, activity_tag: activities.addCompany.tag };

      // Normalize call into (mode, type)
      let mode: 'rush' | 'asset' | null = null;

      let type: ?string = null;

      if (a2 && typeof a2 === 'object' && !Array.isArray(a2)) {
        // Object style: userAddCompany(payload, { mode, type } | { locationType, accountStore, forceMode })
        const opts = a2;
        if (opts.mode) {
          mode = opts.mode; // 'rush' | 'asset'
          type = opts.type || opts.locationType; // 'customer' | 'delivery'
        } else {
          const forceMode = opts.forceMode || null;

          const deduced =
            !forceMode && opts.accountStore ? this._deduceModeFromAccount(opts.accountStore) : null;

          mode = forceMode || deduced || null;
          type = opts.locationType || null;
        }
      } else {
        // (payload, isAssetRequest, locationType, isRushAssetRequest, rushAssetRequestLocation)

        const isAssetRequest: boolean = Boolean(a2);
        const assetType: ?string = a3;
        const isRush: boolean = Boolean(a4);
        const rushType: ?string = a5;

        if (isRush && (rushType === 'customer' || rushType === 'delivery')) {
          mode = 'rush';
          type = rushType;
        } else if (isAssetRequest && (assetType === 'customer' || assetType === 'delivery')) {
          mode = 'asset';
          type = assetType;
        }
      }

      this._applyLocationFlags(wire, mode, type);
      this.response = await this.api.createResource(location.company, wire);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }
    this.loading = false;
  }

  @action
  async userAddLocation(
    formInputs: Object,
    isAssetRequest: Boolean,
    locationType = null,
    isRushAssetRequest: Boolean,
    rushAssetRequestLocation = null
  ): Promise<void> {
    this.loading = true;

    formInputs.activity_tag = activities.addLocation.tag; // eslint-disable-line  camelcase

    formInputs.asset_request_location = isAssetRequest;

    formInputs.asset_request_location_type = locationType;

    formInputs.rush_asset_request_location = isRushAssetRequest;

    formInputs.rush_asset_request_location_type = rushAssetRequestLocation;

    try {
      this.response = await this.api.createResource(location.locations, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async archiveManufacturer(manufacturerGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.archived.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(
        `manufacturers/${manufacturerGuid}/archive`,
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async unarchiveManufacturer(manufacturerGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.unarchived.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(
        `manufacturers/${manufacturerGuid}/unarchive`,
        formInputs
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async archiveProduct(productGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.archived.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(`products/${productGuid}/archive`, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async unarchiveProduct(productGuid: string, formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.unarchived.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.putResource(`products/${productGuid}/unarchive`, formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async userAddCostCenter(formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.addCostCenter.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.createResource('cost-centres', formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async userAddProject(formInputs: Object): Promise<void> {
    this.loading = true;
    formInputs.activity_tag = activities.addProject.tag; // eslint-disable-line  camelcase

    try {
      this.response = await this.api.createResource('projects', formInputs);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async removeDomain(domainId: string): Promise<void> {
    this.loading = true;

    try {
      this.response = await this.api.deleteResource(`blocked-email-domains/${domainId}`);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async removeBrand(brandId: string): Promise<void> {
    this.loading = true;

    try {
      this.response = await this.api.deleteResource(`brands/${brandId}`);
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }

  @action
  async removeCustomerAccount(
    customerAccount: CustomerAccount,
    dataSource: DataSource
  ): Promise<void> {
    this.loading = true;

    try {
      this.response = await this.api.deleteResource(
        `customer-account-management/${dataSource}/${customerAccount.customerId}/${
          customerAccount.companyCode
        }`
      );
      this.success = true;
    } catch (e) {
      this.error = true;
      this.errorResponse = this.api.oAuthStore.errorResponse;
    }

    this.loading = false;
  }
}
